/* eslint-disable react/no-danger -- Rich text is sanitized with DOMPurify before rendering. */
import React, { useState, useEffect } from 'react';
import { connect } from 'umi';
import { Carousel, DatePicker, Form, Select } from 'antd';
import { DatePicker as DatePickerMobile } from 'antd-mobile';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { CalendarOutlined, CloseOutlined } from '@ant-design/icons';

import { get, isEmpty } from 'lodash';
import classnames from 'classnames';
import { Text, Divider, Button } from '@digitalc/dxp-ui';
import useGCPI18n from '@/hooks/useGCPI18n';
import { numberFormat, onHistoryPush } from '@/utils/utils';
// import IMessage from '@/utils/IMessage';
import { BaseSpin, Clickable } from '@/components';
// import defaultImg from '@/assets/images/point/bg@2x.png';
import outOfStock from '@/assets/images/point/outOfStock.png';
import { trackSensors } from '@/utils/sensors';
import '@digitalc/dxp-ui/umd/dxp-ui.min.css';
import Header from '../../../Header';
import styles from './index.less';

const PartnerMerchantsDetails = props => {
  // const { productListitem, skuListItem, userInfo, pointDetail, pointUrl, loading = false } = props;
  const {
    productListitem,
    skuListItem,
    userInfo,
    pointDetail,
    pointAccountsInfo,
    pointAcctTypeCode,
    loading = false,
    dxpToken,
    isMulPoints,
    isWeb,
    category,
  } = props;
  const GCPI18n = useGCPI18n();
  // const initAppStr = window.localStorage.getItem('initApp');
  // const initApp = JSON.parse(initAppStr) || {};
  const [selectedPointType, setSelectedPointType] = useState({});
  const [form] = Form.useForm();
  const [selectedEffType, setSelectedEffType] = useState('');
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [tempDateTime, setTempDateTime] = useState(null); // 临时存储DatePicker滚动选择的值

  // Effective Time Type，要进行国际化，所以放在这里了，不然就是放在const常量文件里面
  const EFFECTIVE_TIME_TYPE = [
    { value: 'A', label: GCPI18n({ id: 'lb_offer_efftype_immediately' }) },
    { value: 'B', label: GCPI18n({ id: 'lb_offer_efftype_next_day' }) },
    { value: 'C', label: GCPI18n({ id: 'lb_offer_efftype_next_month' }) },
    { value: 'D', label: GCPI18n({ id: 'lb_offer_efftype_specified_day' }) },
    { value: 'E', label: GCPI18n({ id: 'lb_offer_efftype_next_billing_cycle' }) },
    { value: 'F', label: GCPI18n({ id: 'lb_offer_efftype_next_week' }) },
  ];

  // 设置默认值为次日00:00:00
  const getDefaultDateTime = () => dayjs().add(1, 'day').startOf('day');

  // 获取默认时间的Date对象（用于antd-mobile）
  const getDefaultDateTimeAsDate = () => dayjs().add(1, 'day').startOf('day').toDate();

  // 解析effType字符串，生成下拉选项
  const getEffTypeOptions = () => {
    const effTypeStr = get(skuListItem, 'effType', '');
    if (!effTypeStr) return [];

    const effTypeValues = effTypeStr.split('|').filter(Boolean);
    return EFFECTIVE_TIME_TYPE.filter(item => effTypeValues.includes(item.value));
  };

  // 判断是否显示effType下拉框
  const shouldShowEffTypeSelect = () => {
    const supportEffTypeSelection = get(skuListItem, 'supportEffTypeSelection', false);
    const isRoamingPackage = get(skuListItem, 'isRoamingPackage', false);
    const effTypeOptions = getEffTypeOptions();

    // supportEffTypeSelection为false，不显示
    if (!supportEffTypeSelection) return false;

    // supportEffTypeSelection为true，isRoamingPackage为false，不显示
    if (!isRoamingPackage) return false;

    // isRoamingPackage为true，但只有一个选项，不显示
    if (effTypeOptions.length <= 1) return false;

    return true;
  };

  // 判断是否显示整个表单区域（effType和DatePicker）
  const shouldShowFormArea = () => get(skuListItem, 'supportEffTypeSelection', false);

  // 初始化effType默认值
  useEffect(() => {
    if (skuListItem) {
      const supportEffTypeSelection = get(skuListItem, 'supportEffTypeSelection', false);
      const effTypeValues = get(skuListItem, 'effType', '')?.split('|').filter(Boolean);

      if (supportEffTypeSelection && effTypeValues?.length > 0) {
        const defaultValue = effTypeValues[0];
        setSelectedEffType(defaultValue);
        form.setFieldsValue({ effType: defaultValue });
      }
    }
  }, [skuListItem]);

  // 当effType变为D时，设置默认时间
  useEffect(() => {
    if (selectedEffType === 'D' && !isWeb) {
      const defaultDate = getDefaultDateTimeAsDate();
      setSelectedDateTime(defaultDate);
      form.setFieldsValue({ dateTime: defaultDate });
    }
  }, [selectedEffType, isWeb]);

  const handleModalSubmit = () => {
    trackSensors('reward_detail', {
      category,
      action: 'button_click',
      label: 'next',
      product_name: productListitem?.name || '',
      productskuid: skuListItem?.skuCode || '',
    });

    let pathname = '/pointshop/orderConfirmation';
    if (isWeb) {
      pathname = pathname.replace('/orderConfirmation', '/pointsH5/modal/orderConfirmation');
    }

    // 获取表单值
    const formValues = form.getFieldsValue();

    // 格式化时间，兼容 Web(dayjs对象) 和 H5(Date对象)
    let formattedDateTime = null;
    if (formValues.dateTime) {
      if (dayjs.isDayjs(formValues.dateTime)) {
        // Web端：dayjs对象
        formattedDateTime = formValues.dateTime.format('YYYY-MM-DD HH:mm:ss');
      } else if (formValues.dateTime instanceof Date) {
        // H5端：Date对象
        formattedDateTime = dayjs(formValues.dateTime).format('YYYY-MM-DD HH:mm:ss');
      }
    }

    // 跳转到确认支付页面
    onHistoryPush(
      pathname,
      {},
      {
        userInfo,
        skuListItem,
        productListitem,
        category,
        effType: selectedEffType,
        absEffDate: formattedDateTime,
      },
    );
  };

  useEffect(() => {
    if (pointAcctTypeCode && !isEmpty(pointAccountsInfo)) {
      const selectedPointTypeTemp = pointAccountsInfo.find(
        item => item.pointAccountTypeCode === pointAcctTypeCode,
      );
      setSelectedPointType(selectedPointTypeTemp);
    }
  }, [pointAccountsInfo, pointAcctTypeCode]);

  // const handleEarnClick = () => {
  //   const jumpObj = {
  //     url: `${window.location.origin}${pointUrl}`,
  //     schemeType: 3, // schemaType 迁移到git需修改！！！
  //   };
  //   if (window.clp) {
  //     window.clp?.openAppUrl(JSON.stringify(jumpObj));
  //   } else {
  //     IMessage.error('Something wrong, try again later!');
  //   }
  // };

  return (
    <div className={classnames(styles.pubDetailBox, isWeb && styles.webPubDetailBox)}>
      <Header className={styles.header} title="" notNewPage />
      <BaseSpin spinning={loading}>
        <div className={styles.detailWrap}>
          {skuListItem && (
            <div className={styles.detailBox}>
              <div className={styles.imageBox}>
                {get(skuListItem, 'inventoryNum') < 1 &&
                  get(skuListItem, 'offerSubType') === 'C' && (
                    <div className={styles.outOfStockWrap}>
                      <img src={outOfStock} alt="" />
                    </div>
                )}
                {(get(skuListItem, 'imageUrlList') || [])?.length > 1 ? (
                  <Carousel autoplay>
                    {(get(skuListItem, 'imageUrlList') || []).map((item, index) => (
                      <img key={index} src={item} alt="" />
                    ))}
                  </Carousel>
                ) : (
                  <img src={get(skuListItem, 'imageUrlList[0]')} alt="" />
                )}
                {/* <img src={get(skuListItem, 'imageUrlList[0]')} alt="" /> */}
                {/* <Carousel autoplay>
                  {(get(skuListItem, 'imageUrlList') || [defaultImg]).map((item, index) => (
                    <img key={index} src={item} alt="" />
                  ))}
                </Carousel> */}
              </div>
              <div className={styles.detailInfo}>
                <div className={styles.infoTop}>
                  {/* <div className={styles.infoName}>
                  </div> */}
                  <Text
                    breakpoint="Mobile"
                    size="TitleT15Bold"
                    style={{ color: dxpToken.colorTextNeutral }}
                  >
                    {get(skuListItem, 'name')}
                  </Text>
                  {/* {get(skuListItem, 'inventoryNum') !== null && (
                    // <div className={styles.remain}>{get(skuListItem, 'inventoryNum') || 0} Left</div>
                    <Text breakpoint="Mobile" size="TitleT21Bold">{get(skuListItem, 'inventoryNum') || 0} Left</Text>
                  )} */}
                  {/* {(get(skuListItem, 'inventoryNum') < 1 && get(skuListItem, 'offerSubType') === 'C') && (
                    <Text breakpoint="Mobile" size="ContentC14Bold" style={{ color: 'red', marginLeft: '0.08rem' }}>Out of stock</Text>
                  )} */}
                </div>
                {shouldShowFormArea() && (
                  <Form form={form} layout="vertical" style={{ marginTop: '0.08rem' }}>
                    {shouldShowEffTypeSelect() && (
                      <Form.Item
                        name="effType"
                        label={GCPI18n({ id: 'lb_offer_effective_type' })}
                        rules={[
                          {
                            required: true,
                            message: GCPI18n({ id: 'lb_offer_effective_type_required' }),
                          },
                        ]}
                      >
                        <Select
                          onChange={value => setSelectedEffType(value)}
                          options={getEffTypeOptions()}
                        />
                      </Form.Item>
                    )}

                    {/* {!shouldShowEffTypeSelect() && getEffTypeOptions().length === 1 && (
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.16rem' }}>
                        <span style={{ marginRight: '0.12rem' }}>{GCPI18n({ id: 'lb_offer_effective_type_label' })}</span>
                        <span
                          style={{
                            backgroundColor: '#ff4d4f',
                            color: '#fff',
                            padding: '0.04rem 0.12rem',
                            borderRadius: '0.04rem',
                            fontSize: '0.14rem',
                          }}
                        >
                          {getEffTypeOptions()[0]?.label}
                        </span>
                      </div>
                    )} */}

                    {selectedEffType === 'D' && (
                      <>
                        {isWeb ? (
                          // Web端使用antd DatePicker
                          <Form.Item
                            name="dateTime"
                            label={GCPI18n({ id: 'lb_offer_effective_time' })}
                            initialValue={getDefaultDateTime()}
                            rules={[
                              {
                                required: true,
                                message: GCPI18n({ id: 'lb_offer_effective_time_required' }),
                              },
                            ]}
                          >
                            <DatePicker
                              showTime
                              allowClear={false}
                              format="YYYY-MM-DD HH:mm:ss"
                              style={{ width: '100%' }}
                              placeholder={GCPI18n({ id: 'lb_offer_effective_time_placeholder' })}
                              disabledDate={current => current && current < dayjs().startOf('day')}
                              disabledTime={current => {
                                if (!current) return {};
                                const now = dayjs();
                                // 如果选择的是今天，需要禁用当前时间之前的时间
                                if (current.isSame(now, 'day')) {
                                  const currentHour = now.hour();
                                  const currentMinute = now.minute();
                                  const currentSecond = now.second();

                                  return {
                                    disabledHours: () =>
                                      Array.from({ length: currentHour }, (_, i) => i),
                                    disabledMinutes: selectedHour => {
                                      if (selectedHour === currentHour) {
                                        return Array.from({ length: currentMinute }, (_, i) => i);
                                      }
                                      return [];
                                    },
                                    disabledSeconds: (selectedHour, selectedMinute) => {
                                      if (
                                        selectedHour === currentHour &&
                                        selectedMinute === currentMinute
                                      ) {
                                        return Array.from(
                                          { length: currentSecond + 1 },
                                          (_, i) => i,
                                        );
                                      }
                                      return [];
                                    },
                                  };
                                }
                                return {};
                              }}
                            />
                          </Form.Item>
                        ) : (
                          // H5端使用antd-mobile DatePicker
                          <Form.Item
                            name="dateTime"
                            label={GCPI18n({ id: 'lb_offer_effective_time' })}
                            initialValue={getDefaultDateTimeAsDate()}
                            rules={[
                              {
                                required: true,
                                message: GCPI18n({ id: 'lb_offer_effective_time_required' }),
                              },
                            ]}
                          >
                            <Clickable
                              onClick={() => {
                                setDatePickerVisible(true);
                                // 打开弹窗时，初始化临时值为当前选中的值
                                setTempDateTime(selectedDateTime || getDefaultDateTimeAsDate());
                              }}
                              className={styles.dateTimeWrap}
                            >
                              <div>
                                {selectedDateTime
                                  ? dayjs(selectedDateTime).format('YYYY-MM-DD HH:mm:ss')
                                  : dayjs(getDefaultDateTimeAsDate()).format('YYYY-MM-DD HH:mm:ss')}
                              </div>
                              <CalendarOutlined style={{ fontSize: '0.24rem', color: '#858585' }} />
                            </Clickable>
                            <DatePickerMobile
                              precision="second"
                              min={new Date()}
                              visible={datePickerVisible}
                              className={styles.datePickerMobileWrap}
                              defaultValue={selectedDateTime || getDefaultDateTimeAsDate()}
                              onClose={() => {
                                setDatePickerVisible(false);
                                setTempDateTime(null);
                              }}
                              onSelect={val => {
                                setTempDateTime(val);
                              }}
                              cancelText={<CloseOutlined className={styles.closeWrap} />}
                              confirmText=""
                              title={GCPI18n({ id: 'lb_offer_installation_datetime' })}
                              style={{
                                '--item-height': '0.44rem', // 单项高度
                                // '--height': '176px', // 容器高度 = 单项高度 * 4 = 44px * 4
                              }}
                            >
                              {() => null}
                            </DatePickerMobile>
                            {datePickerVisible && (
                              <div className={styles.datePickerFooter}>
                                <Button
                                  type="primary"
                                  onClick={() => {
                                    // 点击Confirm时保存临时值到正式值
                                    const finalDateTime =
                                      tempDateTime ||
                                      selectedDateTime ||
                                      getDefaultDateTimeAsDate();
                                    setSelectedDateTime(finalDateTime);
                                    form.setFieldsValue({ dateTime: finalDateTime });
                                    setDatePickerVisible(false);
                                    setTempDateTime(null);
                                  }}
                                  style={{ width: '100%' }}
                                >
                                  {GCPI18n({ id: 'lb_offer_confirm' })}
                                </Button>
                              </div>
                            )}
                          </Form.Item>
                        )}
                      </>
                    )}
                  </Form>
                )}
              </div>
              <Divider type="eight" />
              <div className={styles.mainContentBox}>
                {/* {get(skuListItem, 'pointPrice') > Number(get(pointDetail, 'usablePoint')) && pointUrl.length > 0 && (
                  <div className={styles.contentTip}>
                    <ExclamationCircleOutlined />
                    {GCPI18n({ id: 'POINT_EARN_TIP' })}{' '}
                    <span onClick={handleEarnClick}>{GCPI18n({ id: 'POINT_EARN_POINT' })}</span>.
                  </div>
                )} */}
                <div className={styles.descContent}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(get(skuListItem, 'desc') || ''),
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          <div className={styles.orderFooter}>
            <div className={styles.pointWrap}>
              {/* <Amount showIcon>{initApp.currencyUnit}{numberFormat(get(skuListItem, 'pointPriceDisplay'))}</Amount>
              {
                get(skuListItem, 'pointAcctTypeName') && (
                  <div className={styles.pointTypeName}>
                    <Text breakpoint="Mobile" size="ContentC12Bold">{get(skuListItem, 'pointAcctTypeName')}</Text>
                  </div>
                )
              } */}
              <Text
                breakpoint="Mobile"
                size="ContentC21Bold"
                style={{ color: dxpToken.colorTextAmount }}
              >{`${numberFormat(get(skuListItem, 'pointPriceDisplay'))} `}
              </Text>
              <Text
                breakpoint="Mobile"
                size="ContentC21Bold"
                style={{ color: dxpToken.colorTextAmount }}
              >
                {isMulPoints && get(skuListItem, 'pointAcctTypeName')
                  ? get(skuListItem, 'pointAcctTypeName')
                  : GCPI18n({ id: 'lb_converison_points' })}
              </Text>
            </div>

            <Button
              type="primary"
              onClick={handleModalSubmit}
              className={styles.submitBtn}
              // inventoryNum 为 null 是没关联库存，可以订购，不为null且小于等于0是没有库存，不可以订购
              disabled={
                get(skuListItem, 'pointPrice') >
                  Number(
                    get(
                      isEmpty(selectedPointType) ? pointDetail : selectedPointType,
                      'usablePoint',
                    ),
                  ) ||
                (get(skuListItem, 'inventoryNum') && get(skuListItem, 'inventoryNum') <= 0) ||
                (get(skuListItem, 'inventoryNum') < 1 && get(skuListItem, 'offerSubType') === 'C')
              }
            >
              {get(skuListItem, 'inventoryNum') < 1 && get(skuListItem, 'offerSubType') === 'C'
                ? GCPI18n('lb_order_stock')
                : GCPI18n({
                  id: 'lb_offer_detail_redeem',
                })}
            </Button>
            {/* <div className={styles.footerBox}>
              <div className={styles.orderPoint}>
                {get(skuListItem, 'pointPriceDisplay')} {get(skuListItem, 'pointPrice') === 1 ? 'Point' : 'Points'}
              </div>
              <div className={styles.orderSubmit}>
                <Button
                  type="primary"
                  onClick={handleModalSubmit}
                  className={styles.submitBtn}
                  // inventoryNum 为 null 是没关联库存，可以订购，不为null且小于等于0是没有库存，不可以订购
                  disabled={
                    get(skuListItem, 'pointPrice') > Number(get(pointDetail, 'usablePoint')) ||
                    (get(skuListItem, 'inventoryNum') && get(skuListItem, 'inventoryNum') <= 0)
                  }
                >
                  {GCPI18n({
                    id: 'ORDER_REDEEM',
                  })}
                </Button>
              </div>
            </div> */}
          </div>
        </div>
      </BaseSpin>
    </div>
  );
};

const mapDispatchToProps = () => ({});

export default connect(
  ({ pointShop, loading, index }) => ({
    pointDetail: pointShop.pointDetail,
    pointAccountsInfo: pointShop.pointAccountsInfo,
    dxpToken: index.dxpToken,
    isMulPoints: index.isMulPoints,
    isWeb: index.isWeb,
    loading:
      loading.effects['pointShop/getPointDetail'] || loading.effects['pointShop/getPointDetailDXP'],
  }),
  mapDispatchToProps,
)(PartnerMerchantsDetails);
